<template>
    <div class="product-finder recommendations">
        <ProgressBar></ProgressBar>

        <div class="recommendations-container">

            <div class="recommendations-details">
                <h2>Your Seating Recommendation</h2>
                <p>Based on the preferences and needs, we've curated a selection of therapeutic chairs that offer the best support and comfort*.</p>

                <ul v-if="productFinderStore.resultProducts.length">
                    <li class="recommendations-item" v-for="(product, index) in productFinderStore.resultProducts" :key="index">
                        <figure v-if="product.productImage && product.productImage.length">
                            <picture loading="lazy">
                                <source 
                                    media="(min-width: 768px)" 
                                    :srcset="product.productImage[0]?.largeUrl"
                                >
                                <source 
                                    media="(min-width: 480px)" 
                                    :srcset="product.productImage[0]?.mediumUrl"
                                >
                                <img 
                                    :src="product.productImage[0]?.defaultUrl" 
                                    :alt="product.title" 
                                    loading="lazy"
                                />
                            </picture>
                        </figure>
                        <div class="text">
                            <h3>
                                <span class="text" v-html="product.title"></span>
                                <span class="trademark">&trade;</span>
                            </h3>
                            <ul class="features" v-if="product.productFeatures && product.productFeatures.length">
                                <li v-for="(feature, featureIndex) in product.productFeatures" :key="featureIndex" v-html="feature"></li>
                            </ul>
                            <a class="btn btn-tertiary" aria-label="Full Product Details" :href="product.url">Full Product Details</a>
                        </div>
                    </li>
                </ul>

                <p v-else>No products found.</p>
                <p class="note">*Please note that these results are intended as an initial recommendation only. It is always important to consult with an expert for a thorough evaluation.</p>

                <button class="btn btn-primary" @click="productFinderStore.startAgain()">Start Again</button>

            </div>

            <div class="request-form">
                <header v-if="sent == false">
                    <h2>Talk to a Seating Expert Today.</h2>
                    <p>Our specialists are ready to help you find the ideal therapeutic seating solution. Complete the form below, and we’ll be in touch.</p>
                </header>

                <ValidationObserver v-slot="{ handleSubmit, errors }" v-if="sent == false">

                    <div v-if="freeformData.formTitle" v-html="freeformData.formTitle"></div>

                    <form @submit.prevent="onSubmit" id="freeform" method="POST" class="Recommendations">
                        <input type="hidden" name="freeform_payload" :value="freeformData.freeform_payload" />
                        <input type="hidden" name="formHash" :value="freeformData.hash" />
                        <input type="hidden" name="freeform-action" value="submit" />
                        <input type="hidden" :name="freeformData.csrf.name" :value="csrfToken" />
                        <input type="hidden" name="action" :value="freeformData.action" />

                        <div class="container-xs donate-container">
                            <div class="donate-buttons-block">

                                <fieldset v-for="(row) in freeformFieldsLayout">
                                    <ValidationProvider v-for="field in row" class="field field-wrapper" tag="div" :rules="{ required: true }" :id="field.handle" :name="field.label" v-slot="{ errors }" v-if="field.type != ('html')">
                                        <label :for="field.handle" v-html="field.label + (field.required ? '*' : '')"></label>

                                        <input v-if="field.type == ('text')" :type="field.type" :name="field.handle" v-model="inputModel[field.handle]" :aria-required="field.required">
                                        <input v-else-if="field.type == 'phone'" type="tel" :name="field.handle" v-model="inputModel[field.handle]" pattern="[0-9]*" :aria-required="field.required">
                                        <input v-else-if="field.type == 'email'" type="email" :name="field.handle" v-model="inputModel[field.handle]" :aria-required="field.required">
                                        <select v-else-if="field.type === 'dropdown'" :name="field.handle" v-model="inputModel[field.handle]" :aria-required="field.required">
                                            <option value="" selected disabled>Please select</option>
                                            <option v-for="option in field.options" :key="option.value" :value="option.value">{{ option.label }}</option>
                                        </select>
                                        <textarea v-else-if="field.type == 'textarea'" type="text" :name="field.handle" v-model="inputModel[field.handle]" :aria-required="field.required"></textarea>

                                        <small class="error">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </fieldset>

                                <div class="donate-buttons-inner">
                                    <button type="submit" class="btn btn-primary" aria-label="Send Enquiry">Send Enquiry</button>
                                    <small v-for="(item, index) in submissionErrors" :key="index" data-v-5066735b="" class="error" v-html="item[0]"></small>
                                </div>

                                <div class="note">
                                    By submitting this form, you agree to the <a href="/legal/terms-conditions">Terms &amp; Conditions</a> and <a href="/legal/privacy-policy">Privacy Policy</a>.
                                </div>
                            </div>
                        </div>

                        <input type="hidden" name="answeredQuestions" :value="JSON.stringify(answeredQuestions)" />
                        <input type="hidden" name="products" :value="JSON.stringify(productFinderStore.resultProducts)" />
                    </form>

                </ValidationObserver>

                <div v-if="sent == true">
                    <h3>Thank you for your submission</h3>
                    <p>We will be in touch shortly.</p>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>

    import ProgressBar from './ProgressBar.vue';

    import { onMounted, ref } from 'vue';
    import { useRoute } from 'vue2-helpers/vue-router';
    import { useProductFinderStore } from '../../stores/product-finder';;


    const route = useRoute();
    const productFinderStore = useProductFinderStore();
    productFinderStore.loadStateFromLocalStorage();

    const inputModel = ref(window.freeformFieldsLayoutInputModel || {});
    const freeformData = ref(window.freeformData || {});
    const freeformFieldsLayout = ref(window.freeformFieldsLayout || {});
    const csrfToken = ref(window.craftCsrfToken || {});
    const submissionErrors = ref({});
    const sent = ref(false);
    const loading = ref(false);
    const answeredQuestions = ref(productFinderStore.selectedOptions || {})

    onMounted(() => {
        productFinderStore.map = window.seatingSolutionFinderMap;
    });


    /**
     * Validate Google reCAPTCHA v3.
     * This function dynamically loads the reCAPTCHA script if not already loaded,
     * then executes it to get a token.
     */
    const validateGoogleRecaptcha = async () => {
        // Check if grecaptcha is already available
        if (typeof window.grecaptcha === 'undefined') {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=6Ld3jrAqAAAAAPeQUemBqiheyep5u13S3XnMLi0S`;
            document.head.appendChild(script);
            await new Promise(resolve => {
                script.onload = resolve;
            });
        }
        // Execute reCAPTCHA with your site key and action name
        return window.grecaptcha.execute('6Ld3jrAqAAAAAPeQUemBqiheyep5u13S3XnMLi0S', { action: 'submit' });
    };

    const onSubmit = async () => {
        try {
            const recaptchaResponse = await validateGoogleRecaptcha();
            if (recaptchaResponse) {
                submitForm(recaptchaResponse);
            } else {
                console.error("Recaptcha validation failed.");
            }
        } catch (error) {
            console.error("Error during reCAPTCHA validation:", error);
        }
    };

    const submitForm = (recaptchaResponse) => {
        const form = document.getElementById('freeform');
        const data = new FormData(form);

        loading.value = true;

        // Append the reCAPTCHA response to the form data
        data.append('g-recaptcha-response', recaptchaResponse);

        axios({
                url: form.getAttribute('action') || '/freeform/submit',
                method: form.getAttribute('method') || 'post',
                data: data,
                headers: {
                    'X-CSRF-Token': freeformData.value.csrf.token,
                    'Cache-Control': 'no-cache',
                    'X-Requested-With': 'XMLHttpRequest',
                    'HTTP_X_REQUESTED_WITH': 'XMLHttpRequest',
                    'X-Craft-Solspace-Freeform-Mode': 'Headless',
                },
                withCredentials: true,
            })
            .then(function(responseObject) {
                const response = responseObject.data;
                if (response.success && response.finished) {
                    if (freeformData.value.settings.behavior.returnUrl) {
                        window.location = freeformData.value.settings.behavior.returnUrl;
                    }
                } else if (response.errors || response.formErrors) {
                    loading.value = false;
                    console.log('Field Errors', response.errors);
                    console.log('Form Errors', response.formErrors);
                    submissionErrors.value = response.errors || response.formErrors;
                }
            })
            .catch(function(error) {
                console.error(error);
            });
    };

</script>

<style lang="scss" scoped>
.product-finder {

    .recommendations-container {
        display: flex;
        flex-direction: column;
        position: relative;
        border-radius: 5px;
        background: #FFF;
        box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.05);
        max-width: 1410px;
        margin: 100px auto 0;

        @media (min-width: 1024px) {
            flex-direction: row;
            > * {
                width: 50%;
            }
        }

        .recommendations-details {
            padding: 30px 20px;
            background: #FFF;

            @media (min-width: 550px) {
                padding: 60px;
            }
        }

        .recommendations-item {
            display: flex;
            align-items: flex-start;
            flex-direction: column;
            padding: 30px 0;
            border-bottom: 1px solid #EBEBEB;

            @media (min-width: 550px) {
                flex-direction: row;
            }

            &:first-child{
                padding-top: 0;
            }

            figure{
                max-width: 150px;
                margin-right: 20px;

                @media (min-width: 768px) {
                    margin-right: 20px;
                }
            }

            .features{

                li{
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    color: #333;
                    padding-left: 0;
                    font-weight: 325;
                    font-size: 14px;
                    line-height: normal;

                    @media (min-width: 550px) {
                        color: #5E627A;
                        font-size: 16px;
                        line-height: 26px;
                    }


                    &:before{
                        content: '';
                        display: block;
                        position: relative;
                        left: 0;
                        top: 7px;
                        width: 15px;
                        height: 12px;
                        background-repeat: no-repeat;
                        background-position: 50% 50%;
                        background-size: 100% 100%;
                        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='15' height='12' viewBox='0 0 15 12' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M2.19554 4.07106L0.0742188 6.19238L3.60937 9.72754L5.7307 11.8489L5.73107 11.8492L7.85239 9.72792L7.85202 9.72754L14.9231 2.65647L12.8018 0.535151L5.7307 7.60622L2.19554 4.07106Z' fill='%23B2CCD8'/%3E%3C/svg%3E");
                    }
                }
            }

            h3{
                font-size: 24px;

                @media (min-width: 1024px) {
                    font-size: 28px;
                }
            }

            .btn{
                color: #1A2041 !important;
                text-decoration: underline;
                text-decoration-skip-ink: none;
                text-decoration-line: underline !important;
                text-decoration-thickness: 1px;
                text-decoration-color: #1A2041;
                font-weight: 450;
                text-underline-offset: 2px;
                transition: all 0.3s ease-in-out;
        
                &:hover {
                    text-decoration-color: transparent;
                }

                &:before,
                &:after{
                    display: none;
                }
            }
        }

        .note{
            display: block;
            font-size: 12px;
            margin-top: 25px;
        }

        .request-form {
            padding: 20px;
            background: #B7E3FF;
            @media (min-width: 1024px) {
                padding: 60px;
            }
            form {

                fieldset {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    @media (min-width: 600px){
                        flex-direction: row;
                        gap: 30px;
                    }
                }

                .field {
                    width: 100%;
                    display: flex;
                    flex-direction: column;

                    input {
                        flex: 1;
                    }

                    .error {
                        width: 100%;
                    }
                }
            }
        }
        
    }
}
</style>
